import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  BarChart3, 
  RefreshCw, 
  AlertCircle,
  Package,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { useStore } from '../utils/StoreContext';
import { AnalyticsService, DailyMovementAnalytics, DailyMovementData } from '../services/analyticsService';

interface DailyMovementTrackerProps {
  className?: string;
}

const DailyMovementTracker: React.FC<DailyMovementTrackerProps> = ({
  className = ''
}) => {
  const { user } = useAuth();
  const { currentStore } = useStore();
  
  const [analytics, setAnalytics] = useState<DailyMovementAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDays, setSelectedDays] = useState(5);

  // Load analytics data
  const loadAnalytics = async () => {
    if (!user?.id || !currentStore?.id) return;

    try {
      setLoading(true);
      setError(null);
      
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - selectedDays + 1);
      
      const data = await AnalyticsService.getDailyMovementAnalytics(
        user.id,
        currentStore.id,
        AnalyticsService.formatDate(startDate),
        AnalyticsService.formatDate(endDate),
        selectedDays
      );
      
      setAnalytics(data);
    } catch (err) {
      console.error('Error loading daily movement analytics:', err);
      setError('Failed to load daily movement analytics');
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount and when dependencies change
  useEffect(() => {
    loadAnalytics();
  }, [user?.id, currentStore?.id, selectedDays]);

  // Handle days selection change
  const handleDaysChange = (days: number) => {
    setSelectedDays(days);
  };

  // Get maximum values for scaling bars
  const getMaxValues = (dailyData: DailyMovementData[]) => {
    let maxIn = 0;
    let maxOut = 0;
    
    dailyData.forEach(day => {
      maxIn = Math.max(maxIn, day.movements.in.total);
      maxOut = Math.max(maxOut, day.movements.out.total);
    });
    
    return { maxIn, maxOut };
  };

  // Render movement type segments within a bar
  const renderMovementSegments = (movements: any, total: number, isIncoming: boolean): React.ReactElement[] | null => {
    if (total === 0) return null;

    const segments: React.ReactElement[] = [];
    let position = 0;

    if (isIncoming) {
      const types = [
        { key: 'opening_stock', value: movements.opening_stock, color: 'bg-indigo-500' },
        { key: 'purchase_in', value: movements.purchase_in, color: 'bg-blue-500' },
        { key: 'adjustment_in', value: movements.adjustment_in, color: 'bg-green-500' },
        { key: 'delivery_return', value: movements.delivery_return, color: 'bg-purple-500' }
      ];

      types.forEach(type => {
        if (type.value > 0) {
          const width = (type.value / total) * 100;
          segments.push(
            <div
              key={type.key}
              className={`absolute top-0 h-full ${type.color}`}
              style={{ left: `${position}%`, width: `${width}%` }}
              title={`${AnalyticsService.getMovementTypeInfo(type.key).name}: ${type.value}`}
            />
          );
          position += width;
        }
      });
    } else {
      const types = [
        { key: 'delivery_out', value: movements.delivery_out, color: 'bg-orange-500' },
        { key: 'adjustment_out', value: movements.adjustment_out, color: 'bg-red-500' }
      ];

      types.forEach(type => {
        if (type.value > 0) {
          const width = (type.value / total) * 100;
          segments.push(
            <div
              key={type.key}
              className={`absolute top-0 h-full ${type.color}`}
              style={{ left: `${position}%`, width: `${width}%` }}
              title={`${AnalyticsService.getMovementTypeInfo(type.key).name}: ${type.value}`}
            />
          );
          position += width;
        }
      });
    }

    return segments;
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Daily Movement Tracker</h3>
          <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
        </div>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded mb-4"></div>
          <div className="h-64 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-2">
            <div className="h-3 bg-gray-200 rounded"></div>
            <div className="h-3 bg-gray-200 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-red-200 p-6 ${className}`}>
        <div className="flex items-center space-x-2 text-red-600 mb-2">
          <AlertCircle className="w-5 h-5" />
          <h3 className="text-lg font-semibold">Error Loading Analytics</h3>
        </div>
        <p className="text-red-600 text-sm mb-4">{error}</p>
        <button
          onClick={loadAnalytics}
          className="px-3 py-1 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!analytics) {
    return null;
  }

  const { daily_data: dailyData, summary } = analytics;
  const { maxIn, maxOut } = getMaxValues(dailyData);
  const maxValue = Math.max(maxIn, maxOut);

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
            <BarChart3 className="w-5 h-5" />
            <span>Daily Movement Tracker</span>
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Stock movements over the last {selectedDays} days
          </p>
        </div>
        <button
          onClick={loadAnalytics}
          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          title="Refresh data"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>



      {/* Days Selector */}
      <div className="flex items-center space-x-2 mb-6">
        <Calendar className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-600">Period:</span>
        <div className="flex space-x-1">
          {[5, 7, 14, 30].map((days) => (
            <button
              key={days}
              onClick={() => handleDaysChange(days)}
              className={`px-3 py-1 text-xs rounded transition-colors ${
                selectedDays === days
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {days}d
            </button>
          ))}
        </div>
      </div>

      {/* Chart - Horizontal Layout */}
      <div className="space-y-6">
        {/* Chart Container */}
        <div className="relative" style={{ height: '200px' }}>
          {/* Y-Axis Labels */}
          <div className="absolute left-0 top-0 bottom-0 w-12 flex flex-col justify-between text-xs text-gray-500">
            <div className="text-right pr-2">{maxValue}</div>
            <div className="text-right pr-2">{Math.round(maxValue / 2)}</div>
            <div className="text-right pr-2">0</div>
            <div className="text-right pr-2">-{Math.round(maxValue / 2)}</div>
            <div className="text-right pr-2">-{maxValue}</div>
          </div>

          {/* Chart Area */}
          <div className="ml-14 h-full flex items-end space-x-2">
            {dailyData.map((day, index) => {
              const inHeight = maxValue > 0 ? Math.max((day.movements.in.total / maxValue) * 80, 2) : 0;
              const outHeight = maxValue > 0 ? Math.max((day.movements.out.total / maxValue) * 80, 2) : 0;
              
              return (
                <div key={day.date} className="flex-1 flex flex-col items-center" style={{ height: '180px' }}>
                  {/* Incoming Bar (Top) */}
                  <div className="flex-1 flex items-end justify-center" style={{ height: '80px' }}>
                    {day.movements.in.total > 0 && (
                      <div className="relative group">
                        <div
                          className="relative bg-gray-200 rounded-t-sm transition-all duration-300 hover:brightness-110"
                          style={{ 
                            height: `${inHeight}px`, 
                            width: '24px',
                            minHeight: '4px' 
                          }}
                        >
                          {renderMovementSegments(day.movements.in, day.movements.in.total, true)}
                        </div>
                        {/* Tooltip */}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                          <div className="flex items-center space-x-1">
                            <ArrowUp className="w-3 h-3" />
                            <span>In: {day.movements.in.total}</span>
                          </div>
                          <div className="text-xs text-gray-300">
                            {day.movements.in.opening_stock > 0 && `Opening: ${day.movements.in.opening_stock} `}
                            {day.movements.in.purchase_in > 0 && `Purchase: ${day.movements.in.purchase_in} `}
                            {day.movements.in.adjustment_in > 0 && `Adj+: ${day.movements.in.adjustment_in} `}
                            {day.movements.in.delivery_return > 0 && `Return: ${day.movements.in.delivery_return}`}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Center Line */}
                  <div className="h-px bg-gray-300 w-full"></div>

                  {/* Outgoing Bar (Bottom) */}
                  <div className="flex-1 flex items-start justify-center" style={{ height: '80px' }}>
                    {day.movements.out.total > 0 && (
                      <div className="relative group">
                        <div
                          className="relative bg-gray-200 rounded-b-sm transition-all duration-300 hover:brightness-110"
                          style={{ 
                            height: `${outHeight}px`, 
                            width: '24px',
                            minHeight: '4px' 
                          }}
                        >
                          {renderMovementSegments(day.movements.out, day.movements.out.total, false)}
                        </div>
                        {/* Tooltip */}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                          <div className="flex items-center space-x-1">
                            <ArrowDown className="w-3 h-3" />
                            <span>Out: {day.movements.out.total}</span>
                          </div>
                          <div className="text-xs text-gray-300">
                            {day.movements.out.delivery_out > 0 && `Delivery: ${day.movements.out.delivery_out} `}
                            {day.movements.out.adjustment_out > 0 && `Adj-: ${day.movements.out.adjustment_out}`}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Net Movement Indicator */}
                  <div className="mt-2 text-center">
                    <div className={`text-xs font-medium ${
                      day.movements.net_movement > 0 
                        ? 'text-green-600' 
                        : day.movements.net_movement < 0 
                        ? 'text-red-600' 
                        : 'text-gray-500'
                    }`}>
                      {day.movements.net_movement > 0 ? '+' : ''}{day.movements.net_movement}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* X-Axis Labels (Days) */}
        <div className="ml-14 flex space-x-2">
          {dailyData.map((day) => (
            <div key={day.date} className="flex-1 text-center">
              <div className={`text-xs font-medium ${day.is_today ? 'text-blue-600' : 'text-gray-900'}`}>
                {day.day_short}
              </div>
              <div className="text-xs text-gray-500">
                {new Date(day.date).getDate()}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-2 gap-6">
          {/* Incoming Legend */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center space-x-1">
              <ArrowUp className="w-4 h-4 text-green-600" />
              <span>Incoming</span>
            </h4>
            <div className="space-y-1 text-xs">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-indigo-500 rounded-sm"></div>
                <span>Opening Stock</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded-sm"></div>
                <span>Purchase In</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-sm"></div>
                <span>Adjustment +</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-purple-500 rounded-sm"></div>
                <span>Delivery Return</span>
              </div>
            </div>
          </div>

          {/* Outgoing Legend */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center space-x-1">
              <ArrowDown className="w-4 h-4 text-red-600" />
              <span>Outgoing</span>
            </h4>
            <div className="space-y-1 text-xs">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-orange-500 rounded-sm"></div>
                <span>Delivery Out</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-sm"></div>
                <span>Adjustment -</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyMovementTracker;
